import React, { useState } from "react";
import { X } from "lucide-react";
import { useT } from "./i18n";

const DOCS = {
  tos: {
    title: "Terms of Service",
    version: "v1.1",
    date: "08.08.2026",
    body: null,
  },
  privacy: {
    title: "Privacy Policy (GDPR)",
    version: "v1.1",
    date: "08.08.2026",
    body: null,
  },
  saft: {
    title: "Token Purchase Agreement (SAFT)",
    version: "v2.2",
    date: "08.08.2026",
    body: null,
  },
  cookie: {
    title: "Cookie Policy",
    version: "v1.1",
    date: "08.08.2026",
    body: null,
  },
  aml: {
    title: "AML / KYC Disclosure",
    version: "v1.1",
    date: "08.08.2026",
    body: null,
  },
};

// STEP 17 — Refreshed pre-launch legal accord bodies (Global TGC-Bank).
const STEP17_BODIES = {
  tos: `TURBO GLOBAL COIN LTD. — TERMS OF SERVICE
Global TGC-Bank · Pre-Launch Program · v1.1 · 08.08.2026

1. SERVICE ACTIVATION FEES
Entry activation fees ($10 USDT for PROVIDER, $50 USDT for MERCHANT_RETAIL, $250 USDT for MERCHANT_ENTERPRISE) are designated exclusively for the immediate software activation of the platform's AI Tools suite: AI Corporate Lawyer, AI HR Auditor, Turbo-Zarplata Payroll Engine, AI Recruiter, Turbo AI Support (self-learning RAG), and the Smart Tax ISO-20022 Reserve.

2. HYBRID TOKEN BACKING
100 % of every activation fee is instantly converted into locked TGC tokens on the Corporate Token Ledger under the Fiat Stability Lock (see the SAFT for the full high-watermark protocol).

3. JURISDICTION & GOVERNING LAW
This Agreement is exclusively governed by English Common Law as codified within the Astana International Financial Centre (AIFC). All disputes shall be adjudicated by the AIFC Court and the International Arbitration Centre (IAC) — no other court or arbitration forum has jurisdiction.

4. NO REFUNDS ON ACTIVATION
Because entry fees provision live AI compute resources within seconds of receipt, activation payments are non-refundable. Users retain full withdrawal rights on their TGC snowball position after Mainnet launch, protected by the historical peak fiat (High-Watermark) minimum.

5. ACCEPTABLE USE
Users may not abuse the AI Tools for illegal purposes, bulk-scraping, sanctions evasion, or automated tariff-matrix deviation (see the AML Disclosure).`,

  privacy: `TURBO GLOBAL COIN LTD. — PRIVACY POLICY (GDPR)
Global TGC-Bank · Pre-Launch Program · v1.1 · 08.08.2026

1. DATA SUBJECT RIGHTS
GDPR Articles 15-22 access, rectification, erasure, restriction, portability and objection are honoured through https://turboglobalcoin.com/privacy-request within 30 days.

2. ENCRYPTION STANDARDS
Corporate documents uploaded through the Zero-Legal Compliance module (KYB packets, licences, incorporation certificates, board resolutions) are protected with AES-256-GCM at rest. Only a SHA-256 commitment fingerprint is stored server-side (Zero-Knowledge KYB) — the platform CANNOT read the plaintext of your legal filings.

3. TELEGRAM PARAMETERS
Telegram Bot identifiers, chat memberships and username strings used by the gate at Step 5/13 are hashed with SHA-256 before storage. The raw Telegram user_id never lives at rest on our infrastructure.

4. RETENTION
Compliance artefacts: 10 years. Session cookies: 30 days. Marketing consent: revocable instantly. All backups are AES-256 encrypted and geographically pinned to AIFC-approved data centres.

5. CROSS-BORDER TRANSFERS
Data transfers outside the EEA/AIFC use Standard Contractual Clauses (2021/914) and Transfer Impact Assessments as reviewed by our DPO.`,

  saft: `TURBO GLOBAL COIN LTD. — TOKEN PURCHASE AGREEMENT (SAFT)
Global TGC-Bank · Pre-Launch Program · v2.2 · 08.08.2026

1. HYBRID TGC DEPOSIT
100 % of the buyer's activation fee (10 / 50 / 250 USDT) is atomically converted into locked TGC tokens on the Corporate Token Ledger at the pre-launch reference price (0.11 USDT / TGC at genesis). No burnable-airdrop model applies.

2. VOLATILITY PROTECTION PROTOCOL — FIAT STABILITY LOCK
For every position, the platform maintains a fiat_stability_high_watermark equal to the maximum fiat value the position ever reached, ratcheted upward monotonically by the 0.5 % Turbo-SWIFT transaction burn. If the TGC market price undergoes a downward correction, the buyer's extractable balance is mathematically frozen at the historical peak — the system is prohibited from returning a value below the high-watermark.

3. WITHDRAWAL EVENT
TGC tokens become extractable to any Polygon-compatible wallet immediately upon Mainnet launch. Withdrawals honour max(current_fair_value, fiat_stability_high_watermark) as the minimum withdrawable amount, expressed in USDT and settled on-chain within 0.15 s (Turbo-SWIFT B2B channel).

4. NO SPECULATIVE SECURITY
This SAFT is a software-activation instrument, not an investment security. Purchase does not confer equity, dividend, or governance rights in Turbo Global Coin Ltd.

5. RISK ACKNOWLEDGEMENT
The buyer acknowledges the pre-launch nature of the ecosystem and the fiat stability guarantee is enforced strictly by the platform's smart-contract + off-chain oracle stack.`,

  cookie: `TURBO GLOBAL COIN LTD. — COOKIE POLICY
Global TGC-Bank · Pre-Launch Program · v1.1 · 08.08.2026

1. STRICTLY NECESSARY COOKIES (no consent required)
• tgc_session      — secure session storage token for the authenticated dashboard (HttpOnly, Secure, SameSite=Strict, 30-day TTL).
• tgc_google_ident — persistent link token for Google Sign-In OAuth 2.0 callbacks.
• tgc_mm_conn      — MetaMask persistent connection profile (chain_id + wallet_address hash), keyed to Polygon Mainnet 137.

2. ANALYTICS & FUNCTIONAL (consent required)
Anonymised device fingerprint, tier-selection state (selected_registration_profile), Turbo-AI-Support locale preference. Consent is captured through the site banner and revocable in your account settings.

3. NO ADVERTISING TRACKERS
The platform does not run third-party advertising cookies. No cross-site retargeting pixels are loaded.

4. TOOLING
All cookies listed above are enumerated in your browser's DevTools → Storage panel and can be individually purged at any time.`,

  aml: `TURBO GLOBAL COIN LTD. — AML / KYC DISCLOSURE
Global TGC-Bank · Pre-Launch Program · v1.1 · 08.08.2026

1. MULTI-TIER SCREENING LOGIC (POLYGON MAINNET)
Every inbound payment on Polygon (chain 137) is screened by amount, origin wallet, chain-of-custody, and the corresponding tariff-matrix tier ($10 / $50 / $250 USDT). Any automated inbound payload routing that deviates from the structured tariff matrix — dust attacks, non-round amounts, unlisted contract callers, sanctioned-wallet touch — is immediately flagged, quarantined, and referred to the compliance officer for review.

2. FATF ALIGNMENT
The platform enforces FATF Recommendations 15 and 16 with Travel Rule metadata attached to every settlement above the applicable threshold.

3. SANCTIONS SCREENING
All wallet addresses are screened against OFAC, EU, UN, UK HMT and AIFC watch-lists in real-time. Positive hits are frozen and reported to the AIFC Financial Services Regulator within 24 h.

4. CORPORATE KYB
MERCHANT_ENTERPRISE customers undergo the Zero-Legal KYB flow (see Privacy Policy §2) including UBO declaration (25 %+), incorporation certificate, board resolution, and continuous transaction monitoring.

5. INDIVIDUAL KYC
PROVIDER tier uses ZK-KYC (zero-knowledge proof of identity) for privacy-preserving verification.

6. REPORTING
Suspicious Activity Reports (SARs) are dispatched to the appropriate FIUs. Users have no right to advance notification of an SAR.`,
};

function getDocBodies(t) {
  // STEP 17 — Pre-launch Global TGC-Bank legal bodies always take precedence
  // over the legacy i18n translation strings.
  return {
    tos:     STEP17_BODIES.tos     || t("legal.tos.body"),
    privacy: STEP17_BODIES.privacy || t("legal.privacy.body"),
    saft:    STEP17_BODIES.saft    || t("legal.saft.body"),
    cookie:  STEP17_BODIES.cookie  || t("legal.cookie.body"),
    aml:     STEP17_BODIES.aml     || t("legal.aml.body"),
  };
}

export function LegalDocModal({ docKey, onClose }) {
  const { t } = useT();
  const bodies = getDocBodies(t);
  const baseDoc = DOCS[docKey];
  if (!baseDoc) return null;
  const doc = { ...baseDoc, body: bodies[docKey] };
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", zIndex: 500,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }} data-testid="legal-modal">
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#0B0B10", border: "1px solid var(--tg-border-hi)", borderRadius: 20,
        maxWidth: 760, width: "100%", maxHeight: "90vh", overflow: "hidden",
        display: "flex", flexDirection: "column", boxShadow: "0 30px 80px rgba(0,242,255,0.15)",
      }}>
        <div style={{ padding: "20px 28px", borderBottom: "1px solid var(--tg-border)", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 14 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "var(--tg-text)" }}>{doc.title}</h3>
            <div style={{ fontSize: 12, color: "var(--tg-text-hint)", marginTop: 4 }}>
              <span className="tg-eyebrow" style={{ padding: "2px 10px", marginRight: 8 }}>{doc.version}</span>
              {doc.date}
            </div>
          </div>
          <button onClick={onClose} data-testid="legal-modal-close" style={{
            background: "rgba(255,255,255,0.06)", border: "1px solid var(--tg-border)",
            color: "var(--tg-text)", borderRadius: 10, padding: 8, cursor: "pointer",
          }}><X size={18} /></button>
        </div>
        <div style={{ padding: 28, overflowY: "auto", flex: 1 }}>
          <pre style={{
            whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14, lineHeight: 1.7,
            color: "var(--tg-text-dim)", margin: 0,
          }}>{doc.body}</pre>
        </div>
        <div style={{ padding: "16px 28px", borderTop: "1px solid var(--tg-border)", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "var(--tg-text-hint)" }}>
          <span>© Turbo Global Coin Ltd., Hong Kong</span>
          <a href={`/api/legal/${docKey}`} target="_blank" rel="noreferrer" style={{ color: "var(--tg-blue)" }}>
            {t("legal.download-pdf")}
          </a>
        </div>
      </div>
    </div>
  );
}

export function useLegalModal() {
  const [openDoc, setOpenDoc] = useState(null);
  const close = () => setOpenDoc(null);
  return { openDoc, setOpenDoc, close };
}